import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { toSlug } from '../../../../common/utils/urlUtils';
import { getCanonicalPartnerName } from '../../../../common/constants';
import { usePartnerConfigList, MappedPartnerConfig } from './usePartnerConfigList';
import { useAppSelector } from '../../../../store/hooks';
import { selectMemberInformation } from '../../../../store/slices/onboardingSlice';
import { isPaidTier } from '../../../../common/utils/MembershipTierAccess';
import { PartnerSource } from '../../../../common/enums';

export type PartnerProfileTier = 'free' | 'paid';

export const resolvePartnerProfile = (
  partnerConfigList: MappedPartnerConfig[],
  routeOrName: string,
  requestedTier: PartnerProfileTier | undefined,
  isPaidSubscription: boolean,
): MappedPartnerConfig | undefined => {
  const isPath = routeOrName.startsWith('/');
  const candidate = isPath ?
    routeOrName.split('/').filter(Boolean).pop() ?? '' :
    routeOrName;
  const targetSlug = toSlug(candidate);
  const matchingPartners = partnerConfigList.filter(
    (partner) => toSlug(
      getCanonicalPartnerName(partner.partnerCode, partner.partnerName),
    ) === targetSlug,
  );

  // No tier gate here: partner access is cumulative since SN-996, so a paid member
  // (including an Edge member who upgraded) may open Edge partners. Whether a partner
  // is locked is decided by `meetsPartnerAccess` in `usePartnerConfigList`.
  let requestedSource: PartnerSource | undefined;
  if (requestedTier === 'free') requestedSource = PartnerSource.Edge;
  if (requestedTier === 'paid') requestedSource = PartnerSource.ServiceNation;
  if (requestedSource) {
    return matchingPartners.find((partner) => partner.partnerSource === requestedSource);
  }

  // Keep old/bookmarked URLs useful. A unique slug is unambiguous; shared slugs default to
  // the record belonging to the member's solution org so they can never resolve by array order.
  if (matchingPartners.length === 1) return matchingPartners[0];
  const memberSource = isPaidSubscription ? PartnerSource.ServiceNation : PartnerSource.Edge;
  return matchingPartners.find((partner) => partner.partnerSource === memberSource);
};

/**
 * Hook to dynamically match partner by route or name and return full partner profile config;
 */
export function usePartnerProfile(routeOrName?: string): MappedPartnerConfig | undefined {
  const location = useLocation();
  const partnerConfigList = usePartnerConfigList();
  const { company } = useAppSelector(selectMemberInformation);
  const subscriptionTier = company?.subscription?.subscriptionTier;
  const isPaidSubscription = isPaidTier(subscriptionTier);

  const partnerProfile = useMemo(() => {
    const routeToUse = routeOrName ?? location.pathname;
    const tierParam = new URLSearchParams(location.search).get('tier');
    const requestedTier = tierParam === 'free' || tierParam === 'paid' ? tierParam : undefined;

    return resolvePartnerProfile(
      partnerConfigList,
      routeToUse,
      requestedTier,
      isPaidSubscription,
    );
  }, [routeOrName, location.pathname, location.search, partnerConfigList, isPaidSubscription]);

  return partnerProfile;
}
